import {
  Component,
  HostListener,
  OnDestroy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MaterialModule } from 'src/app/material.module';
import { TablerIconsModule } from 'angular-tabler-icons';
import {
  Observable,
  Subject,
  of,
} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import {
  Customer,
  CustomersService,
} from 'src/app/services/apps/customers/customers.service';

@Component({
  selector: 'app-attention-global-search',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    TablerIconsModule,
  ],
  templateUrl: './attention-global-search.component.html',
  styleUrl: './attention-global-search.component.scss',
})
export class AttentionGlobalSearchComponent implements OnDestroy {
  private readonly customersService = inject(CustomersService);
  private readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();

  readonly searchCtrl = new FormControl<string | Customer>('', {
    nonNullable: false,
  });

  filteredCustomers$: Observable<Customer[]>;

  constructor() {
    this.filteredCustomers$ = this.searchCtrl.valueChanges.pipe(
      startWith(''),
      map((v) => (typeof v === 'string' ? v : '')),
      debounceTime(250),
      distinctUntilChanged(),
      switchMap((term) => {
        const t = term.trim();
        if (t.length < 1) {
          return of([]);
        }
        return this.customersService.getCustomersPaged(1, 5, t).pipe(
          map((res) =>
            res.success && res.requestResult?.items ? res.requestResult.items : []
          )
        );
      }),
      takeUntil(this.destroy$)
    );
  }

  displayFn = (value: Customer | string | null): string => {
    if (!value || typeof value === 'string') {
      return typeof value === 'string' ? value : '';
    }
    return `${value.firstName ?? ''} ${value.surname ?? ''}`.trim();
  };

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const c = event.option.value as Customer;
    if (!c?.id) {
      return;
    }
    this.searchCtrl.setValue(`${c.firstName ?? ''} ${c.surname ?? ''}`.trim(), {
      emitEvent: false,
    });
    void this.router.navigate(['/apps/customer-account'], {
      queryParams: { id: c.id },
    });
  }

  onEnter(event: Event): void {
    const raw = this.searchCtrl.value;
    const term = typeof raw === 'string' ? raw.trim() : '';
    if (!term) {
      return;
    }
    (event.target as HTMLInputElement)?.blur();
    void this.router.navigate(['/apps/customers'], {
      queryParams: { search: term },
    });
  }

  @HostListener('keydown.escape')
  onEscape(): void {
    this.searchCtrl.setValue('', { emitEvent: true });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
